/* eslint-disable react/no-find-dom-node */
/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import {
	FlexLayout,
	FlexItem,
	Button,
	List,
	ListItem,
	TruncatedText,
	SVGIcon,
	Container,
	Tooltip,
	Flyout,
} from '@waca/ca-ui-toolkit';
import coachMark16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/coach-mark_16.js';
import dismiss16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/close_16.js';
import menuOverflow16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--horizontal_16.js';
import search16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/search_16.js';

import stringResource from 'ba-filter-nls';
import { getTid } from 'utils/tid';

export default class FilterHeader extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		title: PropTypes.string.isRequired,
		onSearch: PropTypes.func,
		onClose: PropTypes.func,
		onMenu: PropTypes.func,
		showSearch: PropTypes.bool,
		showClose: PropTypes.bool,
		showMenu: PropTypes.bool,
		menuTitle: PropTypes.string,
		menu: PropTypes.array,
		menuButtonPressed: PropTypes.bool,
		menuButtonDescribedby: PropTypes.string,
		loading: PropTypes.bool,
		height: PropTypes.number,
		readOnly: PropTypes.bool,
		showMatchCondition: PropTypes.bool,
	};

	static defaultProps = {
		showSearch: true,
		showMenu: true,
		showClose: false,
		menu: [],
		loading: false,
		height: 32,
		readOnly: false,
		showMatchCondition: false,
	};

	state = {
		menuOpen: false,
	};

	_menuButton: Button;

	_renderMenuItem = (index, props) => (
		<ListItem {...props}>
			<TruncatedText
				value={this.props.menu[index].label || ''}
				data-tid={`menuItem${index}`}
			/>
		</ListItem>
	);

	/**
	 * A selection has been made in the menu list
	 */
	_onSelectionChange = event => {
		// Get the item
		const item = this.props.menu[event.index];

		// Focus the '...' button
		this._menuButton.focus();

		// Fire the menu action
		item.fn && item.fn();

		this.setState({ menuOpen: false });
	};

	_renderMenu = () => {
		const { menu, readOnly } = this.props;

		if (this.state.menuOpen && menu && menu.length > 0) {
			return (
				<Flyout
					placement="bottom"
					onClose={() => this.setState({ menuOpen: false })}
					triggerNode={ReactDOM.findDOMNode(this._menuButton)}
					domNodeToAttachTo={ReactDOM.findDOMNode(this._menuButton).parentNode}
				>
					<Container data-tid={getTid('filterHeader', 'menuSection')} minWidth={200}>
						<List
							length={menu.length}
							itemRenderer={this._renderMenuItem}
							selectionMode="single"
							listType="uniform"
							onSelectionChange={this._onSelectionChange}
							readOnly={readOnly}
						/>
					</Container>
				</Flyout>
			);
		}

		return null;
	};

	_onMenu = () => {
		this.setState({ menuOpen: !this.state.menuOpen });
		this.props.onMenu && this.props.onMenu();
	};

	focus() {
		if (this.props.showMenu) {
			this._menuButton.focus();
		}
	}

	render() {
		const {
			title,
			showSearch,
			showClose,
			showMenu,
			onClose,
			menuTitle,
			onSearch,
			loading,
			height,
			readOnly,
			showMatchCondition,
			menuButtonPressed,
			menuButtonDescribedby,
		} = this.props;

		return (
			<FlexLayout
				alignItems="center"
				direction="row"
				data-tid={getTid('filterHeader', 'filterHeader')}
				height={height}
			>
				{showMatchCondition && (
					<FlexItem gutter={0.5}>
						<Tooltip
							data-tid={getTid('filterHeader', 'matchingConditionTooltip')}
							title={stringResource.get('matchingCondition')}
							focusable
						>
							<SVGIcon
								iconId={coachMark16.id}
								intent="positive"
								width={8}
								height={8}
								style={{ 'vertical-align': 'baseline' }}
							/>
						</Tooltip>
					</FlexItem>
				)}
				<FlexItem textAlign="left" grow data-tid={getTid('filterHeader', 'title')}>
					<TruncatedText value={title} />
				</FlexItem>
				{showSearch && (
					<FlexItem margin={[0, 1, 0, 0]}>
						<Button
							data-tid={getTid('filterHeader', 'searchClickable')}
							onClick={onSearch}
							title={stringResource.get('find')}
							aria-label={stringResource.get('find')}
							variant="inline"
							icon={<SVGIcon iconId={search16.id} size="small" />}
							disabled={loading || readOnly}
						/>
					</FlexItem>
				)}
				{showMenu && (
					<FlexItem>
						<Button
							data-tid={getTid('filterHeader', 'menuClickable')}
							ref={btn => {
								this._menuButton = btn;
							}}
							onClick={this._onMenu}
							title={menuTitle || stringResource.get('menu')}
							aria-label={menuTitle || stringResource.get('menu')}
							data-selector="true"
							variant="inline"
							icon={<SVGIcon iconId={menuOverflow16.id} size="small" />}
							disabled={loading || readOnly}
							aria-pressed={menuButtonPressed}
							aria-describedby={menuButtonDescribedby}
						/>
						{this._renderMenu()}
					</FlexItem>
				)}
				{showClose && (
					<FlexItem gutter={[0.5, 0, 0, 1]}>
						<Button
							data-tid={getTid('filterHeader', 'closeClickable')}
							onClick={onClose}
							title={stringResource.get('close')}
							aria-label={stringResource.get('close')}
							variant="icon"
							icon={<SVGIcon iconId={dismiss16.id} size="small" />}
						/>
					</FlexItem>
				)}
			</FlexLayout>
		);
	}
}
